import { useState, useCallback, useMemo, useEffect } from 'react';
import { EmptyPallet, DatabasePallet, UnifiedPallet, isEmptyPallet, isDatabasePallet } from '../types/picking';
import { pickingService } from '../services/pickingService';

interface UsePalletManagementReturn {
  pallets: UnifiedPallet[];
  activePalletSSCC: string | null;
  activePallet: UnifiedPallet | null;
  emptyPallets: EmptyPallet[];
  databasePallets: DatabasePallet[];
  isLoading: boolean;
  setActivePalletSSCC: (sscc: string | null) => void;
  loadPallets: () => Promise<void>;
  createEmptyPallet: (ssccCode?: string) => void;
  convertEmptyPalletToDatabase: (emptyPalletSSCC: string, firstItemData: any) => Promise<void>;
  deleteEmptyPallet: (sscc: string) => void;
}

export function usePalletManagement(pListId: number): UsePalletManagementReturn {
  const [pallets, setPallets] = useState<UnifiedPallet[]>([]);
  const [activePalletSSCC, setActivePalletSSCC] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Load pallets from database and merge with empty pallets
  const loadPallets = useCallback(async () => {
    setIsLoading(true);
    try {
      const databasePalletsData = await pickingService.getPallets(pListId);

      // Map database pallets to DatabasePallet type
      const mappedDatabasePallets: DatabasePallet[] = databasePalletsData.map(pallet => ({
        ...pallet,
        type: 'database' as const
      }));

      // Get SSCCs of database pallets
      const databaseSSCCs = new Set(mappedDatabasePallets.map(p => p.sscc));

      // Preserve existing empty pallets ONLY if they haven't been converted to database pallets
      setPallets(currentPallets => {
        const existingEmptyPallets = currentPallets.filter(p =>
          isEmptyPallet(p) && !databaseSSCCs.has(p.sscc)
        );
        return [...existingEmptyPallets, ...mappedDatabasePallets];
      });
    } catch (error) {
      console.error('Failed to load pallets:', error);
      // Keep existing state on error - don't clear empty pallets
    } finally {
      setIsLoading(false);
    }
  }, [pListId]);

  // Load pallets on mount
  useEffect(() => {
    loadPallets();
  }, [loadPallets]);

  // Create a new empty pallet
  const createEmptyPallet = useCallback((ssccCode?: string) => {
    const sscc = ssccCode || `PALL-${Date.now()}`;

    const newEmptyPallet: EmptyPallet = {
      type: 'empty',
      sscc: sscc,
      ssccLevel: 1,
      created: new Date().toISOString(),
      createdBy: 'User', // TODO: Get from auth context
      items: [],
      totalItems: 0,
      totalQuantity: 0,
      totalWeight: 0
    };

    setPallets(currentPallets => [...currentPallets, newEmptyPallet]);
    setActivePalletSSCC(sscc);
  }, []);

  // Stub for converting empty pallet to database pallet
  // Actual conversion will be handled by ModernPickingDetails component
  const convertEmptyPalletToDatabase = useCallback(
    async (emptyPalletSSCC: string, firstItemData: any) => {
      // This is intentionally a stub
      // ModernPickingDetails will handle the actual conversion by:
      // 1. Calling pickingService to create the pallet in database
      // 2. Calling loadPallets() to refresh the list
      console.log('convertEmptyPalletToDatabase stub called', { emptyPalletSSCC, firstItemData });
    },
    []
  );

  // Delete an empty pallet
  const deleteEmptyPallet = useCallback((sscc: string) => {
    setPallets(currentPallets => {
      const filteredPallets = currentPallets.filter(pallet => pallet.sscc !== sscc);

      // If we deleted the active pallet, set a new active pallet
      if (activePalletSSCC === sscc) {
        const newActivePallet = filteredPallets[0];
        setActivePalletSSCC(newActivePallet?.sscc || null);
      }

      return filteredPallets;
    });
  }, [activePalletSSCC]);

  // Derived state: Find active pallet
  const activePallet = useMemo(() => {
    if (!activePalletSSCC) return null;
    return pallets.find(pallet => pallet.sscc === activePalletSSCC) || null;
  }, [pallets, activePalletSSCC]);

  // Derived state: Filter empty pallets
  const emptyPallets = useMemo(() => {
    return pallets.filter(isEmptyPallet);
  }, [pallets]);

  // Derived state: Filter database pallets
  const databasePallets = useMemo(() => {
    return pallets.filter(isDatabasePallet);
  }, [pallets]);

  return {
    pallets,
    activePalletSSCC,
    activePallet,
    emptyPallets,
    databasePallets,
    isLoading,
    setActivePalletSSCC,
    loadPallets,
    createEmptyPallet,
    convertEmptyPalletToDatabase,
    deleteEmptyPallet
  };
}
